import { useEffect, useMemo, useState } from 'react';
import { useDialog } from '../context/DialogContext';
import friendService from '../services/friendService';
import introductionService from '../services/introductionService';
import Modal, { MODAL_FIELD, MODAL_WELL } from './Modal';
import Button from './Button';

/**
 * IntroduceModal — bridges the user (introducer) → a target profile they're
 * looking at + ONE friend they pick. Implements the spec entry point: open
 * from "INTRODUCE →" on a Portal Page page. The friend picker shows the
 * introducer's full friends list as a horizontal-scrolling chip rail.
 */
export default function IntroduceModal({ isOpen, onClose, target, onSent }) {
  const { showAlert } = useDialog();
  const [friends, setFriends] = useState([]);
  const [loadingFriends, setLoadingFriends] = useState(true);
  const [pickedFriend, setPickedFriend] = useState(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [quota, setQuota] = useState(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSent(false);
    setPickedFriend(null);
    setNote('');
    setSearch('');
    setLoadingFriends(true);
    Promise.all([friendService.getFriends(), introductionService.getQuota()]).then(([fRes, qRes]) => {
      if (fRes.success) {
        const list = (fRes.data?.items || fRes.data || [])
          // Don't let the user pick the very person they're trying to introduce
          .filter((f) => f.userId !== target?.userId);
        setFriends(list);
      }
      if (qRes.success) setQuota(qRes.data);
      setLoadingFriends(false);
    });
  }, [isOpen, target?.userId]);

  const filteredFriends = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return friends;
    return friends.filter((f) => (f.displayName || '').toLowerCase().includes(q) || (f.handle || '').toLowerCase().includes(q));
  }, [friends, search]);

  const remaining = quota?.remaining ?? null;
  const canSend = pickedFriend && target?.userId && !sending && (remaining === null || remaining > 0);

  const submit = async () => {
    if (!canSend) return;
    setSending(true);
    const r = await introductionService.create({
      targetAUserId: target.userId,
      targetBUserId: pickedFriend.userId,
      note: note.trim() || null,
    });
    setSending(false);
    if (!r.success) {
      await showAlert({
        title: 'INTRO FAILED',
        message: r.error || 'Could not send the introduction.',
        type: 'error',
      });
      return;
    }
    setSent(true);
    onSent?.(r.data);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" title="INTRODUCE">
      {sent ? (
        <div className="py-2 text-center font-mono">
          <div className="text-[10px] font-bold tracking-widest text-green-700 mb-2">[ HANDSHAKE INITIATED ]</div>
          <p className="text-sm text-black mb-4" style={{ fontFamily: 'Arial, sans-serif' }}>
            {target?.displayName} and {pickedFriend?.displayName} both need to accept before you become a 3-way connection.
            We'll let everyone know what they decide.
          </p>
          <div className="flex justify-center gap-3 items-center mb-4">
            <MiniAvatar name={target?.displayName} url={target?.avatarUrl} state="pending" />
            <span className="font-mono text-[10px] font-bold tracking-widest text-gray-500">—— PENDING ——</span>
            <MiniAvatar name={pickedFriend?.displayName} url={pickedFriend?.avatarUrl} state="pending" />
          </div>
          <Button onClick={onClose}>DONE</Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-between items-baseline">
            <p className="font-mono text-[10px] font-bold tracking-widest text-gray-500">[ 3-WAY HANDSHAKE ]</p>
            <p className={`font-mono text-[10px] font-bold tracking-widest ${remaining === 0 ? 'text-red-600' : 'text-gray-500'}`}>
              {remaining ?? '?'} / {quota?.limit ?? 3} LEFT THIS WEEK
            </p>
          </div>

          <div className={MODAL_WELL}>
            <div className="font-mono text-[10px] font-bold tracking-widest text-gray-500 mb-1">INTRODUCING</div>
            <div className="flex items-center gap-3">
              <MiniAvatar name={target?.displayName} url={target?.avatarUrl} />
              <div>
                <div className="font-mono text-sm font-bold uppercase tracking-wider">{target?.displayName}</div>
                {target?.handle && <div className="font-mono text-[11px] text-gray-500">@{target.handle}</div>}
              </div>
            </div>
          </div>

          <div>
            <div className="font-mono text-[10px] font-bold tracking-widest text-gray-500 mb-2">TO ONE OF YOUR FRIENDS</div>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="SEARCH YOUR FRIENDS"
              className={`${MODAL_FIELD} mb-2 text-xs uppercase tracking-wider`}
            />
            {loadingFriends ? (
              <div className="text-center py-3 font-mono text-[10px] tracking-widest text-gray-400">LOADING…</div>
            ) : filteredFriends.length === 0 ? (
              <div className="text-center py-3 font-mono text-[10px] tracking-widest text-gray-400">
                {friends.length === 0 ? 'YOU NEED FRIENDS BEFORE YOU CAN INTRODUCE THEM' : 'NO MATCHES'}
              </div>
            ) : (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {filteredFriends.map((f) => {
                  const active = pickedFriend?.userId === f.userId;
                  return (
                    <button
                      key={f.userId}
                      type="button"
                      onClick={() => setPickedFriend(f)}
                      aria-pressed={active}
                      className={`flex-shrink-0 w-20 p-2 rounded-2xl flex flex-col items-center gap-1 transition-colors ${
                        active ? 'bg-[#F2F2F2] ring-2 ring-black' : 'bg-[#F5F5F5] hover:bg-[#E9E9E9]'
                      }`}
                    >
                      <MiniAvatar name={f.displayName} url={f.avatarUrl} small />
                      <div className="font-mono text-[10px] font-bold uppercase tracking-wider truncate w-full text-center">{f.displayName}</div>
                      {f.handle && <div className="font-mono text-[8px] text-gray-400 truncate w-full text-center">@{f.handle}</div>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <label className="block font-mono text-[10px] font-bold tracking-widest text-gray-500 mb-1">NOTE (OPTIONAL)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 240))}
              rows={2}
              placeholder="Why these two should meet…"
              className={`${MODAL_FIELD} resize-none`}
              style={{ fontFamily: 'Arial, sans-serif', fontSize: 13 }}
            />
            <div className="text-right font-mono text-[9px] tracking-widest text-gray-400">{note.length} / 240</div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>CANCEL</Button>
            <Button disabled={!canSend} onClick={submit}>{sending ? 'SENDING…' : 'SEND INTRODUCTION'}</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function MiniAvatar({ name, url, small, state }) {
  const dim = small ? 'w-10 h-10' : 'w-14 h-14';
  return (
    <div className="flex flex-col items-center gap-1">
      <div className={`${dim} rounded-full bg-dreamPink flex items-center justify-center font-mono font-bold text-black overflow-hidden`}>
        {url ? <img src={url} alt={name} className="w-full h-full object-cover" /> : (name || '?').charAt(0).toUpperCase()}
      </div>
      {state && <div className="rounded-full px-2 py-0.5 bg-yellow-400 font-mono text-[8px] font-bold tracking-widest text-black">{state.toUpperCase()}</div>}
    </div>
  );
}
